import { z } from 'zod'
import {
  asBasisPoints,
  asCount,
  asMillilitres,
  asMinutes,
  asPence,
  asWp,
  isBasisPoints,
  isCount,
  isMillilitres,
  isMinutes,
  isPence,
  isWp,
} from './units'

/** Every base unit a payload may declare. Architecture section 4.2. */
export type UnitName = 'pence' | 'basisPoints' | 'wp' | 'millilitres' | 'minutes' | 'count'

/**
 * The only approved reasons a payload may carry a real number. Site coordinates
 * are the one genuine real in the system (architecture section 5.2). Adding a
 * reason is a deliberate core edit that shows up in review AND in the committed
 * events manifest — which is the point. Free text drifts to 'temp' within a phase.
 */
export type DecimalReason = 'site-coordinate'

export type UnitTag =
  | { readonly kind: 'integer'; readonly unit: UnitName }
  | { readonly kind: 'decimal'; readonly reason: DecimalReason }

/**
 * Module-private on purpose.
 *
 * Zod's `.brand()` is type-level only (`def.type` stays `'number'`), so the
 * declared unit has to live somewhere `describeSchema` can read at manifest-build
 * time. A private registry rather than zod's global `.meta()` means nothing
 * outside this file can register a unit, so a unit cannot be forged onto a bare
 * `z.number()` — the same reasoning that puts `Pence` and `Digest` in the
 * brand-forgery lint selector.
 *
 * Verified against zod 4.4.3: the tag lives on the exact schema instance the
 * helper returned. `.refine()` and `.meta()` clone, and zod propagates registry
 * entries onto the clone, so the tag survives those. `.optional()`,
 * `.nullable()`, `z.array()` and `z.union()` do NOT clone — they WRAP — so the
 * wrapper itself carries no tag while the instance it encloses still does.
 * That is the property the descriptor depends on: it unwraps to the leaf and
 * reads the unit there, at any nesting depth.
 */
const units = z.registry<{ readonly tag: UnitTag }>()

export function unitOf(schema: z.ZodType): UnitTag | undefined {
  return units.get(schema)?.tag
}

function tag<S extends z.ZodType>(schema: S, unitTag: UnitTag): S {
  units.add(schema, { tag: unitTag })
  return schema
}

/*
 * Each helper is `z.int()` for integrality and safe range, then the brand's own
 * predicate, then the brand's constructor. The predicate is load-bearing for
 * the bounded brands only (`z.int()` accepts -1; `isCount` does not) and is
 * kept on all six so a change to any brand's bounds takes effect here without
 * an edit. `.transform` mints through the sanctioned constructor: that is a
 * type-level guarantee, not a runtime one, since a brand is an identity cast.
 */

export function zPence() {
  return tag(z.int().refine(isPence, { error: 'must be integer pence' }).transform(asPence), {
    kind: 'integer',
    unit: 'pence',
  })
}

export function zBasisPoints() {
  return tag(
    z.int().refine(isBasisPoints, { error: 'must be non-negative basis points' }).transform(asBasisPoints),
    { kind: 'integer', unit: 'basisPoints' },
  )
}

export function zWp() {
  return tag(z.int().refine(isWp, { error: 'must be non-negative Wp' }).transform(asWp), {
    kind: 'integer',
    unit: 'wp',
  })
}

export function zMillilitres() {
  return tag(
    z.int().refine(isMillilitres, { error: 'must be non-negative millilitres' }).transform(asMillilitres),
    { kind: 'integer', unit: 'millilitres' },
  )
}

export function zMinutes() {
  return tag(z.int().refine(isMinutes, { error: 'must be non-negative minutes' }).transform(asMinutes), {
    kind: 'integer',
    unit: 'minutes',
  })
}

export function zCount() {
  return tag(z.int().refine(isCount, { error: 'must be a non-negative count' }).transform(asCount), {
    kind: 'integer',
    unit: 'count',
  })
}

/**
 * The single named escape for a genuine real. The reason travels into the
 * committed events manifest, so every use is visible in review.
 */
export function zDecimal(reason: DecimalReason) {
  // `z.number()` already refuses NaN and Infinity, which is the whole
  // requirement here — a non-finite value cannot be canonicalised. No
  // refinement is needed, and a redundant one would read as load-bearing.
  return tag(z.number(), {
    kind: 'decimal',
    reason,
  })
}
